import React, { useRef, useState, useCallback } from 'react';

// Subtle hover sound using Web Audio API
let audioCtx = null;
function playHoverSound() {
  try {
    if (!audioCtx) audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    if (audioCtx.state === 'suspended') return; // user hasn't interacted yet
    const osc = audioCtx.createOscillator();
    const gain = audioCtx.createGain();
    osc.type = 'sine';
    osc.frequency.value = 800;
    gain.gain.setValueAtTime(0.015, audioCtx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + 0.15);
    osc.connect(gain);
    gain.connect(audioCtx.destination);
    osc.start();
    osc.stop(audioCtx.currentTime + 0.15);
  } catch {}
}

export default function TiltCard({ children, style, glowColor = 'rgba(93,193,185,0.3)', disabled, ...rest }) {
  const ref = useRef(null);
  const [tilt, setTilt] = useState({ rx: 0, ry: 0, lx: 50, ly: 50, active: false });

  const handleMove = useCallback((e) => {
    if (disabled) return;
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    const rx = (y - 0.5) * -16; // max 8deg
    const ry = (x - 0.5) * 16;
    setTilt({ rx, ry, lx: x * 100, ly: y * 100, active: true });
  }, [disabled]);

  const handleEnter = useCallback(() => {
    if (!disabled) {
      // Init audio context on first user interaction
      if (!audioCtx) {
        try { audioCtx = new (window.AudioContext || window.webkitAudioContext)(); } catch {}
      }
      playHoverSound();
    }
  }, [disabled]);

  const handleLeave = useCallback(() => {
    setTilt({ rx: 0, ry: 0, lx: 50, ly: 50, active: false });
  }, []);

  const baseTransform = tilt.active
    ? `perspective(1000px) rotateX(${tilt.rx}deg) rotateY(${tilt.ry}deg) translateZ(20px) scale(1.02)`
    : 'perspective(1000px) rotateX(0deg) rotateY(0deg) translateZ(0px) scale(1)';

  const lightOverlay = tilt.active
    ? `radial-gradient(circle at ${tilt.lx}% ${tilt.ly}%, rgba(255,255,255,0.12), transparent 60%)`
    : 'none';

  return (
    <div
      ref={ref}
      onMouseMove={handleMove}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      style={{
        ...style,
        transform: baseTransform,
        transition: tilt.active
          ? 'transform 0.1s ease-out, box-shadow 0.2s'
          : 'transform 0.5s ease-out, box-shadow 0.5s ease-out',
        boxShadow: tilt.active
          ? `0 0 40px ${glowColor}, 0 20px 40px rgba(0,0,0,0.3)`
          : (style?.boxShadow || 'none'),
        position: 'relative',
        overflow: 'hidden',
        willChange: 'transform',
      }}
      {...rest}
    >
      {children}
      {/* Light reflection overlay */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
        background: lightOverlay,
        pointerEvents: 'none',
        borderRadius: 'inherit',
        transition: 'background 0.1s',
      }} />
    </div>
  );
}
